class ProductForm extends HTMLElement {
  connectedCallback () {
    if (this.controller) return
    this.form = this.querySelector('form')
    if (!this.form) return
    this.controller = new AbortController()
    const options = { signal: this.controller.signal }
    this.idInput = this.form.querySelector('[name="id"]')
    this.productOptions = this.form.querySelectorAll('.product-options [name*="option"]')
    this.atcBtn = this.form.querySelector('.btn-atc')
    this.buyBtn = this.form.querySelector('.btn-buy, [data-buy-now]')
    this.quantity = this.form.querySelector('input[name="quantity"]')
    try { this.variants = JSON.parse(this.form.dataset.variants || '[]') } catch (_) { this.variants = [] }
    this.form.addEventListener('submit', event => this.onSubmit(event), options)
    this.idInput?.addEventListener('change', () => this.syncPurchaseState(), options)
    this.productOptions.forEach(elem => elem.addEventListener('change', event => this.onChangeProductOption(event), options))
    this.buyBtn?.addEventListener('click', event => this.onClickBuyBtn(event), options)
    this.quantity?.addEventListener('input', () => this.syncPurchaseState(), options)
    this.quantity?.addEventListener('change', () => this.syncPurchaseState(), options)
    this.form.querySelector('[data-qty-minus]')?.addEventListener('click', () => this.changeQuantity(-1), options)
    this.form.querySelector('[data-qty-plus]')?.addEventListener('click', () => this.changeQuantity(1), options)
    this.syncPurchaseState()
  }

  disconnectedCallback () {
    this.controller?.abort()
    this.controller = null
  }

  selectedVariant () {
    return this.variants.find(variant => String(variant.id) === this.idInput?.value)
  }

  getQuantity () {
    const value = Number(this.quantity?.value || 1)
    return Number.isFinite(value) ? Math.max(1, Math.floor(value)) : 1
  }

  changeQuantity (amount) {
    if (this.quantity) this.quantity.value = Math.max(1, this.getQuantity() + amount)
    this.syncPurchaseState()
  }

  syncPurchaseState () {
    const variant = this.selectedVariant()
    const quantity = this.getQuantity()
    if (this.quantity && this.quantity.value !== '') this.quantity.value = quantity
    const minus = this.form.querySelector('[data-qty-minus]')
    if (minus) minus.disabled = quantity <= 1
    this.adjustButtons(variant)
    if (this.buyBtn?.tagName === 'A') {
      if (variant?.available && !this.busy) this.buyBtn.href = `${window.Shopify.routes.cart_url}/${variant.id}:${quantity}?checkout`
      else this.buyBtn.removeAttribute('href')
    }
  }

  onChangeProductOption (event) {
    event.preventDefault()
    const selectedOptions = []
    this.productOptions.forEach(elem => {
      if ((elem.type === 'radio' && elem.checked) || elem.type === 'select-one') selectedOptions.push(elem.value)
    })
    const variant = this.variants.find(item => JSON.stringify(item.options) === JSON.stringify(selectedOptions))
    if (this.idInput) this.idInput.value = variant ? variant.id : ''
    this.adjustPricing(variant)
    this.syncPurchaseState()
    this.showMessage('')
    if (!variant) return
    if (this.closest('.product-main')) {
      const url = new URL(window.location.href)
      url.searchParams.set('variant', variant.id)
      window.history.replaceState(window.history.state, '', url)
    }
    window.dispatchEvent(new CustomEvent('kt.product.variant.change', { detail: { ...variant, productId: this.form.dataset.productid } }))
  }

  adjustPricing (variant) {
    if (!variant) return
    const compare = this.form.querySelector('.product-price-compare')
    const price = this.form.querySelector('.product-price-final')
    const sale = this.form.querySelector('.product-sale-badge')
    const soldOut = this.form.querySelector('.product-sold-out-badge')
    const discounted = variant.compare_at_price > variant.price
    if (price) {
      price.innerHTML = window.Shopify.formatMoney(variant.price)
      price.classList.toggle('product-price-final-sale', discounted)
    }
    if (compare) {
      compare.textContent = discounted ? window.Shopify.formatMoney(variant.compare_at_price) : ''
      compare.hidden = !discounted
    }
    if (sale) {
      sale.hidden = !discounted
      const saving = sale.dataset.discountType === 'percentage' ? Math.round((variant.compare_at_price - variant.price) * 100 / variant.compare_at_price) + '%' : window.Shopify.formatMoney(variant.compare_at_price - variant.price)
      sale.textContent = discounted ? `${window.theme.locales.product.save} ${saving}` : ''
    }
    if (soldOut) soldOut.hidden = variant.available
  }

  adjustButtons (variant) {
    const canBuy = !!variant?.available && !this.busy
    const text = variant ? (variant.available ? window.theme.locales.product.add_to_cart : window.theme.locales.product.sold_out) : window.theme.locales.product.unavailable
    if (this.atcBtn) {
      this.atcBtn.disabled = !canBuy
      const label = this.atcBtn.querySelector('[data-zepuni-atc-text]') || this.atcBtn
      label.textContent = text
    }
    if (this.buyBtn) {
      this.buyBtn.setAttribute('aria-disabled', String(!canBuy))
      if (this.buyBtn.tagName === 'BUTTON') this.buyBtn.disabled = !canBuy
      this.buyBtn.style.opacity = canBuy ? '' : '.5'
    }
    this.form.querySelectorAll('.shopify-payment-button').forEach(button => { button.hidden = !canBuy })
  }

  showMessage (message, added = false) {
    let alert = this.form.querySelector('[data-product-error]')
    if (!alert && message) {
      alert = document.createElement('p')
      alert.setAttribute('data-product-error', '')
      alert.setAttribute('role', 'alert')
      alert.className = 'notranslate text-danger mt-3'
      alert.setAttribute('translate', 'no')
      const block = this.atcBtn?.closest('.product-block-atc')
      ;(block || this.form).append(alert)
    }
    if (!alert) return
    alert.textContent = message
    alert.hidden = !message
    if (added) {
      const link = document.createElement('a')
      link.href = window.Shopify.routes.cart_url
      link.textContent = window.theme.locales.product.view_cart
      alert.append(' ', link)
    }
  }

  async onSubmit (event) {
    event.preventDefault()
    if (!this.atcBtn || this.busy) return
    this.syncPurchaseState()
    if (!this.selectedVariant()?.available) { this.showMessage(window.theme.locales.product.unavailable); return }
    if (this.quantity) this.quantity.value = this.getQuantity()
    this.showMessage('')
    this.busy = true
    this.atcBtn.classList.add('loading')
    this.atcBtn.setAttribute('aria-busy', 'true')
    this.syncPurchaseState()
    try {
      const cart = document.querySelector('cart-container')
      if (!cart?.add) throw new Error(window.theme.locales.product.cart_error)
      const result = await cart.add(new FormData(this.form))
      if (!result.cartRefreshed) this.showMessage(window.theme.locales.product.cart_added_refresh, true)
    } catch (error) {
      this.showMessage(error.shopifyDescription || window.theme.locales.product.cart_error)
    } finally {
      this.busy = false
      this.atcBtn.classList.remove('loading')
      this.atcBtn.setAttribute('aria-busy', 'false')
      this.syncPurchaseState()
    }
  }

  onClickBuyBtn (event) {
    this.syncPurchaseState()
    if (this.busy || !this.selectedVariant()?.available) { event.preventDefault(); return }
    if (this.buyBtn.tagName !== 'A') {
      event.preventDefault()
      window.location.assign(`${window.Shopify.routes.cart_url}/${this.selectedVariant().id}:${this.getQuantity()}?checkout`)
    }
  }
}
customElements.define('product-form', ProductForm)

class ProductMediaGallery extends HTMLElement {
  connectedCallback () {
    if (this.main || !window.Swiper || !this.querySelector('.swiper-main')) return
    const thumbs = this.querySelector('.swiper-thumbs')
    if (thumbs) this.thumbs = new window.Swiper(thumbs, { slidesPerView: Number(this.dataset.thumbsPerView) || 4, spaceBetween: 10 })
    this.main = new window.Swiper(this.querySelector('.swiper-main'), {
      speed: Number(this.dataset.speed) || 300,
      rewind: true,
      a11y: { prevSlideMessage: this.dataset.previousLabel, nextSlideMessage: this.dataset.nextLabel, paginationBulletMessage: this.dataset.paginationLabel },
      pagination: { type: 'bullets', el: this.querySelector('.swiper-pagination'), dynamicBullets: true, dynamicMainBullets: 2 },
      navigation: { nextEl: this.querySelector('.swiper-button-next'), prevEl: this.querySelector('.swiper-button-prev') },
      ...(this.thumbs ? { thumbs: { swiper: this.thumbs, autoScrollOffset: 1 } } : {})
    })
    this.main.slideTo(Number(this.dataset.initialSlide) || 0)
    this.onVariantChange = event => {
      const variant = event.detail
      if (variant.productId && String(variant.productId) !== this.dataset.productId) return
      if (variant.featured_media) this.main.slideTo(variant.featured_media.position - 1)
    }
    window.addEventListener('kt.product.variant.change', this.onVariantChange)
  }

  disconnectedCallback () {
    window.removeEventListener('kt.product.variant.change', this.onVariantChange)
    this.main?.destroy(true, true)
    this.thumbs?.destroy(true, true)
    this.main = this.thumbs = null
  }
}
customElements.define('product-media-gallery', ProductMediaGallery)

const adjustShopifySubscriptionsStyling = () => {
  document.querySelectorAll('.shopify_subscriptions_app_block [type="radio"]').forEach(radio => radio.classList.add('form-check-input'))
}
adjustShopifySubscriptionsStyling()
window.addEventListener('kt.product.quick_view.modal_shown', adjustShopifySubscriptionsStyling)
